import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { Page404 } from '../pages/auth/Page404';

type ErrorBoundaryState = {
  error: null | Record<string, any>;
  errorInfo: null | {
    componentStack: string;
  };
};

type ErrorBoundaryProps = {
  children: ReactNode;
  location: ReturnType<typeof useLocation>;
  supportEmail: string | null | undefined;
};

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
  }

  componentDidUpdate(
    prevProps: Readonly<ErrorBoundaryProps>,
    prevState: Readonly<ErrorBoundaryState>
  ) {
    const isPathnameChanged =
      this.props.location.pathname !== prevProps.location.pathname;
    const isErrorVisible = !!this.state.error || !!this.state.errorInfo;

    if (isPathnameChanged && isErrorVisible) {
      this.removeError();
    }
  }

  removeError = () => this.setState({ error: null, errorInfo: null });

  render() {
    if (!this.state.errorInfo) {
      return this.props.children;
    }

    const email = this.props.supportEmail ?? 'support@xyte.io';

    return (
      <Page404
        title={['Oops...', 'Something went wrong']}
        description={
          <div>
            Please try again or contact support at{' '}
            <a href={`mailto:${email}`}>{email}</a>
          </div>
        }
      />
    );
  }
}
